/**
 * useDevLoginAs — client half of the ?as=<role> dev-only sign-in pattern.
 *
 * Watches the URL for `?as=<role>` and, when present in a non-production
 * build, calls the dev-login-as edge fn to swap the browser's Supabase
 * session to the corresponding flows201 test user. Strips the param
 * from the URL on success so the history stays clean and refreshing
 * doesn't re-trigger the flow.
 *
 * Production-safety:
 *   1. Short-circuits on !DEMO_ROLE_SWITCHER_ENABLED — the composite
 *      flag collapses to false in production builds so Vite tree-shakes
 *      the entire hook body.
 *   2. Edge fn refuses to run when ENV=production, refuses any email
 *      outside the flows201+ test pool, and is not even deployed to
 *      the production Supabase project.
 *
 * Call site: mount once at the top of <App /> (next to Auth boot) so
 * every route handles the param consistently.
 */

import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { supabase, SUPABASE_URL, SUPABASE_ANON_KEY } from '@/lib/supabase';
import { DEMO_ROLE_SWITCHER_ENABLED } from '@/hooks/useDemoRoleOverride';
import { setDemoRoleOverride } from '@/hooks/useDemoRoleOverride';

const ROLE_QUERY_KEY = 'as';

export function useDevLoginAs() {
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (!DEMO_ROLE_SWITCHER_ENABLED) return;
    const role = searchParams.get(ROLE_QUERY_KEY);
    if (!role) return;

    let cancelled = false;
    (async () => {
      try {
        // Supabase edge fns require an Authorization header even for
        // "public" endpoints — the edge runtime refuses the request with
        // 401 before our handler runs. Pass the anon key as Bearer so
        // the fn is callable, and let the fn's own gates do the
        // production/environment checks.
        const resp = await fetch(`${SUPABASE_URL}/functions/v1/dev-login-as`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
            apikey: SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({ role }),
        });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok) {
          console.error('[useDevLoginAs] failed', data);
          toast.error(
            data?.hint
              ? `Dev login failed: ${data.hint}`
              : `Dev login failed: ${data?.error ?? resp.status}`,
          );
          return;
        }
        if (!data.access_token || !data.refresh_token) {
          toast.error('Dev login returned no session');
          return;
        }
        const { error } = await supabase.auth.setSession({
          access_token: data.access_token,
          refresh_token: data.refresh_token,
        });
        if (error) {
          toast.error(`setSession failed: ${error.message}`);
          return;
        }
        if (cancelled) return;

        // Sync the UI-layer DemoRoleSwitcher pill so the "Viewing as"
        // label reflects the active session. (RLS already sees the
        // real role; this keeps the chrome honest.)
        setDemoRoleOverride(role as any);

        toast.success(`Signed in as ${role}`);

        // Strip the param so refresh doesn't re-trigger.
        const next = new URLSearchParams(searchParams);
        next.delete(ROLE_QUERY_KEY);
        setSearchParams(next, { replace: true });
      } catch (e) {
        console.error('[useDevLoginAs] threw', e);
        toast.error('Dev login errored — see console');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [searchParams, setSearchParams]);
}
